import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { ensureRouteData } from '@/app/query-client'
import { brokersByStockQueryOptions } from '@/features/brokers/api/queries'
import { defaultBrokerStock } from '@/features/brokers/lib/defaultTargets'
import { readLastBrokerStock } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { DEFAULT_PERIOD } from '@/screens/brokers/search'

/**
 * Whether `symbol` is a real, live stock — `true`/`false` for a clean answer,
 * and `false` again for a validating read that itself FAILED (a 5xx, a device
 * that drops offline mid-check): a broken CHECK is not a broken redirect, and
 * must fall through to the market-wide default the same as a confirmed-bad
 * symbol does, rather than surfacing as this route's own error page — only a
 * failure of the DEFAULT lookup below earns that. This IS the destination's
 * own MAIN read on success, so it also warms `ByStockScreen.loader.ts`'s
 * `ensureRouteData` for the same key — one request serves both jobs.
 */
async function validateLastBrokerStock(client: QueryClient, symbol: string): Promise<boolean> {
  try {
    return (
      (await ensureRouteData(client, brokersByStockQueryOptions(symbol, DEFAULT_PERIOD))) !== null
    )
  } catch {
    return false
  }
}

/**
 * The `/brokers/by-stock` loader — the sidebar's "By stock" row when no
 * symbol is in view.
 *
 * VALIDATES last-used before trusting it, not just "read it and go": a symbol
 * remembered from a past visit can go bad (delisted, a typo replayed from
 * storage), and `brokersByStockQueryOptions` now answers a bad symbol with
 * `null` rather than throwing (docs/route-loading.md, "Not-found answers") —
 * exactly the signal `validateLastBrokerStock` needs to fall back to the
 * market-wide default instead of redirecting the member to a page that only
 * says "not found". Falls through to the default exactly ONCE: if the default
 * itself has nothing (`defaultBrokerStock` rejects), that propagates as a
 * real error rather than looping back to try last-used again.
 *
 * `replace: true` so Back does not bounce through this segment. Nothing
 * renders here on a normal visit — the loader always throws, either a
 * redirect or an error the router turns into its own error page, never a
 * blank one.
 *
 * TanStack Router follows a redirect thrown mid-preload to keep warming the
 * destination's data, but never commits it to history — so a hover over the
 * sidebar row runs this loader (last-used validation included) without
 * navigating anywhere; only a click does. Confirmed against
 * `@tanstack/router-core`'s `preloadClientRoute`/`materializeRedirect`: a
 * redirect's target location is re-matched and re-loaded IN THE PRELOAD
 * LANE, never handed to `router.navigate`.
 */
export async function loadByStockRedirect({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<never> {
  const lastUsed = readLastBrokerStock()
  if (lastUsed !== null && (await validateLastBrokerStock(queryClient, lastUsed))) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/brokers/by-stock/$symbol',
      params: { symbol: lastUsed },
      replace: true,
    })
  }

  const symbol = await defaultBrokerStock(queryClient)
  // The default came from Most traded, a different read — warm by-stock's
  // own key for it too, so the redirect lands on cached data.
  await ensureRouteData(queryClient, brokersByStockQueryOptions(symbol, DEFAULT_PERIOD))
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '/brokers/by-stock/$symbol', params: { symbol }, replace: true })
}
